import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { logger } from '@/lib/logger'
import { openDB, type IDBPDatabase } from 'idb'

interface CacheEntry {
  url: string
  blob: Blob
  objectUrl: string
  timestamp: number
  accessCount: number
  lastAccessed: number
  signedUrlExpiresAt: number
}

interface IDBCacheEntry {
  url: string // Primary key (storage path as cache key)
  blob: Blob
  timestamp: number
  accessCount: number
  lastAccessed: number
  blobSize: number
}

interface CacheStats {
  size: number
  hitRate: number
  totalRequests: number
  cacheHits: number
  cacheMisses: number
}

/**
 * Centralized Audio Cache Store with IndexedDB
 * 
 * Features:
 * - Cross-tab cache sharing via IndexedDB
 * - Persistent cache across browser sessions
 * - In-memory objectURL cache for fast lookups
 * - Automatic signed URL expiration handling (Supabase URLs expire in 1 hour)
 * - LRU eviction when cache limit reached
 * - 200MB cache limit for audio files
 */
export const useAudioCacheStore = defineStore('audioCache', () => {
  // In-memory cache for fast objectURL lookups (per-tab)
  const inMemoryCache = ref(new Map<string, CacheEntry>())
  const inFlightRequests = new Map<string, Promise<string>>()
  
  // Track failed paths with exponential backoff to prevent infinite retry loops
  // Map<cacheKey, { failCount: number, nextRetryAt: number }>
  const failedPaths = new Map<string, { failCount: number; nextRetryAt: number }>()
  const MAX_FAIL_COUNT = 5 // Stop retrying after 5 failures
  const BASE_BACKOFF_MS = 5000 // 5 seconds initial backoff
  
  // IndexedDB instance (shared across tabs, persistent)
  let db: IDBPDatabase | null = null
  let dbInitPromise: Promise<void> | null = null
  const DB_NAME = 'ambi-audio-cache'
  const DB_VERSION = 1
  const STORE_NAME = 'audio'
  
  // Configuration - Audio files can be larger so we have a bigger limit
  const maxCacheSize = 200 * 1024 * 1024 // 200MB for audio files
  const maxInMemoryItems = 50 // Limit in-memory cache to 50 items (audio files are larger)
  const maxCacheAge = 7 * 24 * 60 * 60 * 1000 // 7 days for audio blobs (longer than images)
  const signedUrlTTL = 55 * 60 * 1000 // 55 minutes (Supabase URLs expire at 60 min)
  let currentCacheSize = 0
  
  // Version counter for reactivity
  const cacheVersion = ref(0)
  
  // Statistics
  const stats = ref({
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0
  })
  
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Initialize IndexedDB
   */
  const initDB = async (): Promise<void> => {
    if (db) return
    
    if (dbInitPromise) return dbInitPromise

    dbInitPromise = (async () => {
      try {
        db = await openDB(DB_NAME, DB_VERSION, {
          upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_NAME)) {
              const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' })
              store.createIndex('timestamp', 'timestamp')
              store.createIndex('lastAccessed', 'lastAccessed')
              logger.info('[audioCache] Created IndexedDB object store')
            }
          }
        })

        await recalculateCacheSize()
        
        logger.info('[audioCache] IndexedDB initialized', {
          dbName: DB_NAME,
          version: DB_VERSION,
          currentSize: currentCacheSize
        })
      } catch (err) {
        logger.error('[audioCache] Failed to initialize IndexedDB:', err)
        error.value = 'Failed to initialize audio cache database'
        dbInitPromise = null
      }
    })()
    
    return dbInitPromise
  }

  /**
   * Recalculate total cache size from IndexedDB
   */
  const recalculateCacheSize = async (): Promise<void> => {
    if (!db) return

    try {
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const allEntries = await store.getAll()
      
      currentCacheSize = allEntries.reduce((total, entry) => total + (entry.blobSize || 0), 0)
      
      logger.debug('[audioCache] Recalculated cache size:', {
        entries: allEntries.length,
        sizeBytes: currentCacheSize,
        sizeMB: (currentCacheSize / (1024 * 1024)).toFixed(2)
      })
    } catch (err) {
      logger.error('[audioCache] Failed to recalculate cache size:', err)
    }
  }

  /**
   * Get cache statistics
   */
  const getCacheStats = computed((): CacheStats => {
    const size = currentCacheSize
    const totalRequests = stats.value.totalRequests
    const cacheHits = stats.value.cacheHits
    const cacheMisses = stats.value.cacheMisses
    const hitRate = totalRequests > 0 ? (cacheHits / totalRequests) * 100 : 0

    return {
      size,
      hitRate,
      totalRequests,
      cacheHits,
      cacheMisses
    }
  })

  /**
   * Get current cache size in MB
   */
  const getCacheSizeMB = computed(() => {
    return (currentCacheSize / (1024 * 1024)).toFixed(2)
  })

  /**
   * Get number of cached audio files
   */
  const getCacheCount = computed(() => {
    return inMemoryCache.value.size
  })

  /**
   * Check if signed URL has expired
   */
  const isSignedUrlExpired = (expiresAt: number): boolean => {
    return Date.now() >= expiresAt
  }

  /**
   * Extract expiration time from Supabase signed URL
   */
  const extractExpirationFromUrl = (url: string): number => {
    try {
      const urlObj = new URL(url)
      const tokenParam = urlObj.searchParams.get('token')
      if (!tokenParam) {
        return Date.now() + signedUrlTTL
      }
      
      try {
        const parts = tokenParam.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]))
          if (payload.exp) {
            return (payload.exp * 1000) - (5 * 60 * 1000)
          }
        }
      } catch {
        // If JWT parsing fails, fall back to default TTL
      }
      
      return Date.now() + signedUrlTTL
    } catch {
      return Date.now() + signedUrlTTL
    }
  }

  /**
   * Evict least recently used entries from IndexedDB
   */
  const evictLRUFromDB = async (requiredSpace: number): Promise<void> => {
    if (!db) return

    try {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const index = store.index('lastAccessed')
      
      const allEntries = await index.getAll()
      
      for (const entry of allEntries) {
        if (currentCacheSize + requiredSpace <= maxCacheSize) {
          break
        }

        await store.delete(entry.url)
        currentCacheSize -= entry.blobSize
        
        const memEntry = inMemoryCache.value.get(entry.url)
        if (memEntry) {
          URL.revokeObjectURL(memEntry.objectUrl)
          inMemoryCache.value.delete(entry.url)
        }
        
        logger.debug('[audioCache] Evicted LRU entry from IndexedDB:', {
          url: entry.url,
          size: entry.blobSize
        })
      }

      await tx.done
    } catch (err) {
      logger.error('[audioCache] Failed to evict LRU entries:', err)
    }
  }

  /**
   * Evict least recently used entries from in-memory cache
   */
  const evictLRUFromMemory = (): void => {
    if (inMemoryCache.value.size <= maxInMemoryItems) return
    
    const entries = Array.from(inMemoryCache.value.entries())
      .sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed)
    
    const evictCount = Math.max(1, Math.ceil(maxInMemoryItems * 0.2))
    const toEvict = entries.slice(0, evictCount)
    
    for (const [key, entry] of toEvict) {
      URL.revokeObjectURL(entry.objectUrl)
      inMemoryCache.value.delete(key)
    }
    
    logger.debug('[audioCache] Evicted LRU entries from in-memory cache:', {
      evicted: toEvict.length,
      remaining: inMemoryCache.value.size
    })
  }

  /**
   * Remove expired entries from IndexedDB
   */
  const cleanupExpired = async (): Promise<void> => {
    if (!db) return

    const now = Date.now()
    
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      const index = store.index('timestamp')
      
      const allEntries = await index.getAll()
      const expiredKeys: string[] = []

      for (const entry of allEntries) {
        if (now - entry.timestamp > maxCacheAge) {
          expiredKeys.push(entry.url)
          await store.delete(entry.url)
          currentCacheSize -= entry.blobSize
          
          const memEntry = inMemoryCache.value.get(entry.url)
          if (memEntry) {
            URL.revokeObjectURL(memEntry.objectUrl)
            inMemoryCache.value.delete(entry.url)
          }
        }
      }

      await tx.done

      if (expiredKeys.length > 0) {
        logger.info(`[audioCache] Cleaned up ${expiredKeys.length} expired entries`)
      }
    } catch (err) {
      logger.error('[audioCache] Failed to cleanup expired entries:', err)
    }
  }

  /**
   * Convert storage path to a cache key format
   */
  const storagePathToCacheKey = (storagePath: string): string => {
    if (storagePath.startsWith('/storage/')) {
      return storagePath.split('?')[0]
    }
    return `/storage/v1/object/sign/user-audio/${storagePath}`
  }

  /**
   * Check if a path has failed too many times and is in backoff period
   */
  const isInFailedBackoff = (cacheKey: string): boolean => {
    const failed = failedPaths.get(cacheKey)
    if (!failed) return false
    
    // If we've exceeded max retries, block forever (until cache clear)
    if (failed.failCount >= MAX_FAIL_COUNT) {
      return true
    }
    
    // If we're still in backoff period, don't retry
    if (Date.now() < failed.nextRetryAt) {
      return true
    }
    
    return false
  }

  /**
   * Record a failed fetch with exponential backoff
   */
  const recordFailure = (cacheKey: string): void => {
    const existing = failedPaths.get(cacheKey)
    const failCount = (existing?.failCount || 0) + 1
    // Exponential backoff: 5s, 10s, 20s, 40s, 80s...
    const backoffMs = BASE_BACKOFF_MS * Math.pow(2, failCount - 1)
    const nextRetryAt = Date.now() + backoffMs
    
    failedPaths.set(cacheKey, { failCount, nextRetryAt })
    
    logger.warn(`⏸️ [audioCache] Recorded failure #${failCount} for ${cacheKey}, next retry in ${backoffMs/1000}s`)
  }

  /**
   * Clear failure record for a path (call on successful fetch)
   */
  const clearFailure = (cacheKey: string): void => {
    if (failedPaths.has(cacheKey)) {
      failedPaths.delete(cacheKey)
      logger.debug(`✅ [audioCache] Cleared failure record for ${cacheKey}`)
    }
  }

  /**
   * Get audio by storage path - OPTIMIZED VERSION
   * Only requests a signed URL if the audio is not already cached
   * 
   * @param storagePath - The storage path (e.g., "user-id/library/file.mp3")
   * @param bucket - The storage bucket (default: "user-audio")
   */
  const getAudioByPath = async (storagePath: string, bucket: string = 'user-audio'): Promise<string> => {
    if (!storagePath) return ''

    const cacheKey = storagePathToCacheKey(storagePath)
    stats.value.totalRequests++

    // Cleanup expired entries periodically (5% chance - less frequent for audio)
    if (Math.random() < 0.05) {
      cleanupExpired()
    }

    // 0. Check if this path has failed too many times - PREVENT INFINITE RETRY
    if (isInFailedBackoff(cacheKey)) {
      const failed = failedPaths.get(cacheKey)
      logger.debug(`🚫 [audioCache] Skipping failed path (${failed?.failCount} failures):`, { storagePath, cacheKey })
      return ''
    }

    // 1. Check in-memory cache first
    const cached = inMemoryCache.value.get(cacheKey)
    if (cached && !isSignedUrlExpired(cached.signedUrlExpiresAt)) {
      stats.value.cacheHits++
      cached.accessCount++
      cached.lastAccessed = Date.now()
      logger.debug('[audioCache] In-memory cache hit:', { storagePath, cacheKey })
      return cached.objectUrl
    }

    // 2. Check if already being fetched
    let inFlight = inFlightRequests.get(cacheKey)
    if (inFlight) {
      logger.debug('[audioCache] Using in-flight request:', { storagePath, cacheKey })
      return await inFlight
    }

    // 3. Create and SET the promise BEFORE any async work
    const processPromise = processAudioByPath(storagePath, bucket, cacheKey)
    inFlightRequests.set(cacheKey, processPromise)
    
    return await processPromise
  }

  /**
   * Internal function to process audio by path
   */
  const processAudioByPath = async (storagePath: string, bucket: string, cacheKey: string): Promise<string> => {
    await initDB()

    try {
      // Try IndexedDB first
      if (db) {
        try {
          const dbEntry = await db.get(STORE_NAME, cacheKey)
          if (dbEntry) {
            const blobAge = Date.now() - dbEntry.timestamp
            
            if (blobAge < maxCacheAge) {
              const objectUrl = URL.createObjectURL(dbEntry.blob)
              const entry: CacheEntry = {
                url: cacheKey,
                blob: dbEntry.blob,
                objectUrl,
                timestamp: dbEntry.timestamp,
                accessCount: dbEntry.accessCount + 1,
                lastAccessed: Date.now(),
                signedUrlExpiresAt: Date.now() + signedUrlTTL
              }
              
              inMemoryCache.value.set(cacheKey, entry)
              cacheVersion.value++
              evictLRUFromMemory()
              
              // Update access stats in IndexedDB (fire and forget)
              db.put(STORE_NAME, {
                ...dbEntry,
                accessCount: entry.accessCount,
                lastAccessed: entry.lastAccessed
              }).catch(err => logger.warn('[audioCache] Failed to update stats', err))
              
              stats.value.cacheHits++
              logger.debug('[audioCache] IndexedDB cache hit:', { storagePath, cacheKey })
              return objectUrl
            } else {
              // Blob too old, remove it
              await db.delete(STORE_NAME, cacheKey)
              currentCacheSize -= dbEntry.blobSize
            }
          }
        } catch (err) {
          logger.error('[audioCache] IndexedDB lookup error:', err)
        }
      }

      // Cache miss - fetch from network
      stats.value.cacheMisses++
      logger.debug('[audioCache] Cache miss, requesting signed URL:', { storagePath, cacheKey })

      // Get signed URL from provider
      const { getDataProvider } = await import('@/lib/dataProvider')
      const provider = getDataProvider()
      const signedUrl = await provider.getFileUrl(bucket, storagePath, 3600)
      
      if (!signedUrl) {
        throw new Error(`Failed to get signed URL for audio: ${storagePath}`)
      }

      // Fetch the audio file
      const response = await fetch(signedUrl, {
        mode: 'cors',
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.statusText}`)
      }

      const blob = await response.blob()
      const blobSize = blob.size

      // Evict if needed
      if (currentCacheSize + blobSize > maxCacheSize) {
        await evictLRUFromDB(blobSize)
      }

      const objectUrl = URL.createObjectURL(blob)
      const now = Date.now()
      const expiresAt = extractExpirationFromUrl(signedUrl)

      const entry: CacheEntry = {
        url: cacheKey,
        blob,
        objectUrl,
        timestamp: now,
        accessCount: 1,
        lastAccessed: now,
        signedUrlExpiresAt: expiresAt
      }

      inMemoryCache.value.set(cacheKey, entry)
      cacheVersion.value++
      evictLRUFromMemory()

      // Save to IndexedDB
      if (db) {
        try {
          const dbEntry: IDBCacheEntry = {
            url: cacheKey,
            blob,
            timestamp: now,
            accessCount: 1,
            lastAccessed: now,
            blobSize
          }
          
          await db.put(STORE_NAME, dbEntry)
          currentCacheSize += blobSize

          logger.info('[audioCache] Cached audio:', {
            storagePath,
            cacheKey,
            size: blobSize,
            totalSize: currentCacheSize
          })
        } catch (err) {
          logger.error('[audioCache] Failed to save to IndexedDB:', err)
        }
      }

      // Clear any previous failure record on success
      clearFailure(cacheKey)

      return objectUrl
    } catch (err) {
      logger.error('[audioCache] Fetch error:', err)
      // Record failure with exponential backoff to prevent infinite retry
      recordFailure(cacheKey)
      return ''
    } finally {
      inFlightRequests.delete(cacheKey)
    }
  }

  /**
   * Preload multiple audio files by storage path
   */
  const preloadAudioByPath = async (storagePaths: string[], bucket: string = 'user-audio'): Promise<void> => {
    const uniquePaths = [...new Set(storagePaths)].filter(path => {
      if (!path) return false
      const cacheKey = storagePathToCacheKey(path)
      return !inMemoryCache.value.has(cacheKey) && !inFlightRequests.has(cacheKey)
    })
    
    if (uniquePaths.length === 0) {
      logger.debug('[audioCache] All audio files already cached')
      return
    }

    logger.info('[audioCache] Preloading audio files:', {
      total: storagePaths.length,
      toPreload: uniquePaths.length
    })

    isLoading.value = true
    try {
      await Promise.allSettled(uniquePaths.map(path => getAudioByPath(path, bucket)))
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get cached URL by storage path synchronously
   */
  const getCachedUrlByPath = (storagePath: string): string => {
    // Access cacheVersion for reactivity
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    cacheVersion.value
    
    const cacheKey = storagePathToCacheKey(storagePath)
    const entry = inMemoryCache.value.get(cacheKey)
    if (entry?.objectUrl) {
      return entry.objectUrl
    }
    return ''
  }

  /**
   * Check if audio is cached by storage path
   */
  const isCachedByPath = (storagePath: string): boolean => {
    const cacheKey = storagePathToCacheKey(storagePath)
    const entry = inMemoryCache.value.get(cacheKey)
    return entry?.objectUrl !== undefined
  }

  /**
   * Clear a specific audio file from cache
   */
  const clearAudio = async (storagePath: string): Promise<void> => {
    const cacheKey = storagePathToCacheKey(storagePath)
    
    const entry = inMemoryCache.value.get(cacheKey)
    if (entry) {
      URL.revokeObjectURL(entry.objectUrl)
      inMemoryCache.value.delete(cacheKey)
    }
    
    if (db) {
      try {
        const dbEntry = await db.get(STORE_NAME, cacheKey)
        if (dbEntry) {
          await db.delete(STORE_NAME, cacheKey)
          currentCacheSize -= dbEntry.blobSize
        }
      } catch (err) {
        logger.error('[audioCache] Failed to clear from IndexedDB:', err)
      }
    }
    
    cacheVersion.value++
  }

  /**
   * Clear all cached audio files
   */
  const clearCache = async (): Promise<void> => {
    // Revoke all object URLs
    inMemoryCache.value.forEach(entry => {
      URL.revokeObjectURL(entry.objectUrl)
    })
    inMemoryCache.value.clear()
    cacheVersion.value++
    
    if (db) {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        await tx.objectStore(STORE_NAME).clear()
        await tx.done
        logger.info('[audioCache] Cleared all audio from IndexedDB')
      } catch (err) {
        logger.error('[audioCache] Failed to clear IndexedDB:', err)
      }
    }
    
    // Clear failed paths tracking
    failedPaths.clear()
    logger.info('[audioCache] Cleared failed paths tracking')
    
    currentCacheSize = 0
    stats.value = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0
    }
  }

  /**
   * Clear all failed path records (allow retrying)
   */
  const clearFailedPaths = (): void => {
    const count = failedPaths.size
    failedPaths.clear()
    logger.info(`🔄 [audioCache] Cleared ${count} failed path records - retries now allowed`)
  }

  /**
   * Get count of failed paths
   */
  const getFailedPathsCount = (): number => {
    return failedPaths.size
  }

  // Initialize DB on store creation
  initDB()

  return {
    // State
    isLoading,
    error,
    
    // Computed
    getCacheStats,
    getCacheSizeMB,
    getCacheCount,
    
    // Reactivity helper
    cacheVersion,
    
    // Methods - Path based
    getAudioByPath,
    preloadAudioByPath,
    getCachedUrlByPath,
    isCachedByPath,
    
    // Utilities
    storagePathToCacheKey,
    
    // Cache management
    clearAudio,
    clearCache,
    clearFailedPaths,
    getFailedPathsCount
  }
})
